<template>
  <article v-if="author" class="page">
    <section class="section font-roboto">
      <div class="mb-[100px] flex justify-start">
        <div class="flex w-full flex-col md:flex-row">
          <div
            class="flex max-h-[480px] w-full max-w-[720px] overflow-hidden rounded-xl md:w-1/3"
          >
            <img
              v-lazy-load
              :src="getSrc"
              :alt="
                author.image && author.image.alt
                  ? author.image.alt
                  : author.fullname
              "
              class="size-full object-cover"
            />
          </div>
          <div class="mt-8 flex w-full flex-col md:ml-14 md:mt-0 md:w-1/2">
            <h1 class="mb-5 text-[32px] font-medium leading-[38px]">
              {{ author.fullname }}
            </h1>
            <div class="mb-10 grid gap-5 text-[20px] leading-[30px] empty:mb-0">
              <div
                v-if="author.description || author.announce"
                v-html="author.description || author.announce"
              ></div>
              <div v-if="author.activity" class="flex gap-1">
                <strong>Род деятельности:</strong>{{ author.activity }}
              </div>
              <div v-if="author.research_interests" class="flex gap-1">
                <strong>Научные интересы:</strong
                >{{ author.research_interests }}
              </div>
            </div>
            <BaseTheButtonNew
              :text="
                isSubscribed ? 'Отписаться от автора' : 'Подписаться на автора'
              "
              type="secondary"
              @click.stop="handleSubscribe(author)"
            />
          </div>
        </div>
      </div>

      <h2
        class="text-7 mb-7 font-medium leading-[34px] xl:text-[28px] xl:leading-[34px]"
      >
        Материалы автора
      </h2>
      <div class="mb-9 flex w-full flex-col justify-between lg:flex-row">
        <CommonTabs
          :list="author.content_types"
          :initial-tab-index="activeTabIndex"
          @on-change="selectedSortValue = $event"
        />
      </div>
      <div v-if="content?.length">
        <div class="grid grid-cols-1 gap-[20px] md:grid-cols-2 lg:grid-cols-3">
          <MainPageCardsReworkedBaseCard
            v-for="(item, index) in content"
            :key="`article-${item.id}-${index}`"
            class="col-span-1"
            :data="item"
            disable-announce
          />
        </div>
        <div class="my-14 flex justify-center">
          <BaseTheButtonNew
            v-if="authorContent.hasNextPage.value"
            text="Показать еще"
            @click="authorContent.fetchNextPage()"
          />
        </div>
      </div>
      <div
        v-else
        class="my-12 text-center font-roboto text-[32px] font-medium leading-[38px] text-new-gray-lighter"
      >
        В данной категории пока нет материалов
      </div>
    </section>
    <CommonBlocksPageFooterBlocks />
  </article>
</template>

<script setup>
const route = useRoute();
const slug = route?.params?.slug;

const { authorSlug } = useAuthorBySlug(slug);
useErrorHandler(() => authorSlug.failureReason.value?.status);

const author = computed(() => authorSlug?.data?.value);
const content = computed(() => authorContent?.data?.value);

const activeTabIndex = computed(() => {
  const idx = author.value.content_types?.findIndex(
    ({ model }) => model === route.query.content,
  );

  if (idx !== -1) return idx;
  const availableIdx = author.value.content_types?.findIndex(
    ({ count }) => count > 0,
  );

  if (availableIdx !== -1) return availableIdx;
  return 0;
});

const selectedSortValue = computed(
  () => author?.value?.content_types?.[activeTabIndex.value]?.model,
);

const { authorContent } = useAuthorContent(slug, selectedSortValue);

const { isSubscribed, handleSubscribe } = useAuthorSubscribeHandler(author);

const getSrc = computed(() => {
  const src =
    author.value?.image && author.value.image?.src
      ? author.value.image.src
      : null;

  if (!src) return '/imgs/default-image.png';
  return src;
});

useHead(
  computed(() => {
    return {
      title: `${
        author.value?.fullname ? `${author.value?.fullname} | ` : ''
      } Читать исторические документы РФ для школьников и студентов`,
      meta: [
        {
          hid: 'description',
          name: 'description',
          content: `Исторические статьи от автора ${author.value?.fullname}, точно описывающие реальные события, произошедшие в прошлом. Российское военно-историческое общество. Мы знаем правду.`,
        },
      ],
      link: [
        {
          rel: 'canonical',
          href: `${useHostApp()}${route.path}`,
        },
      ],
    };
  }),
);

useSchemaOrg(() =>
  defineArticle({
    datePublished: new Date(author.value?.published_at),
    ...(author.value?.image.is_blank && {
      image: {
        url: `${useHostApp()}${author.value?.image.src}`,
        width: 800,
        height: 600,
        caption: author.value?.image?.alt || '',
      },
    }),
    about: {
      '@type': 'Person',
      name: author.value?.fullname,
      ...(author.value?.image.is_blank && {
        image: {
          '@type': 'ImageObject',
          url: `${useHostApp()}${author.value?.image.preview}`,
          width: 400,
          height: 400,
          caption: author.value?.image.alt,
        },
      }),
    },
  }),
);
</script>

<style lang="scss"></style>
